import { InfiniteTable, InfiniteTableColumns, InfiniteTableSortBy, Pill, Stack, Text } from '@sixfold/common-ui';
import React, { useCallback, useMemo, useState } from 'react';
import { NavLink } from 'react-router';

import { CredentialType } from '../../lib/graphql';
import { beautifyString } from '../../lib/util/string';
import { Routes } from '../../routes';
import { User } from '../entities';

export interface UserListDataProps {
  data: {
    users: User[] | null;
  };
}

interface UserRow {
  id: string;
  user_id: string;
  name: string;
  email: string;
  backoffice_role: string;
  credential_types: CredentialType[];
  credential_types_sort: string;
}

const getSortValue = (row: UserRow, key: keyof UserRow): string | number => {
  switch (key) {
    case 'user_id':
      return parseInt(row.user_id, 10);
    case 'name':
      return row.name;
    case 'email':
      return row.email;
    case 'backoffice_role':
      return row.backoffice_role;
    case 'credential_types_sort':
      return row.credential_types_sort;
    default:
      return '';
  }
};

const sortData = (data: UserRow[], sortBy: InfiniteTableSortBy<keyof UserRow>): UserRow[] => {
  return [...data].sort((a, b) => {
    const aVal = getSortValue(a, sortBy.columnId);
    const bVal = getSortValue(b, sortBy.columnId);
    const cmp =
      typeof aVal === 'number' && typeof bVal === 'number' ? aVal - bVal : String(aVal).localeCompare(String(bVal));
    return sortBy.order === 'ASC' ? cmp : -cmp;
  });
};

export const UserList: React.FC<UserListDataProps> = ({ data }) => {
  const { users } = data;
  const [sortBy, setSortBy] = useState<InfiniteTableSortBy<keyof UserRow>>({
    columnId: 'user_id',
    order: 'ASC',
  });

  const tableData: UserRow[] = useMemo(
    () =>
      sortData(
        (users ?? []).map((user) => {
          const credentialTypes = Array.from(new Set((user.credentials ?? []).map((credential) => credential.type)));
          credentialTypes.sort();

          return {
            id: user.user_id,
            user_id: user.user_id,
            name: [user.first_name, user.last_name].filter((i) => i).join(' '),
            email: user.email ?? '',
            backoffice_role: user.backoffice_role ?? '-',
            credential_types: credentialTypes,
            credential_types_sort: credentialTypes.join(',') || '-',
          };
        }),
        sortBy,
      ),
    [users, sortBy],
  );

  const columns: InfiniteTableColumns<UserRow> = useMemo(
    () => [
      {
        id: 'user_id',
        title: '#',
        width: '6%',
        isUserSortable: true,
        align: 'end' as const,
        cellContent: ({ row }) => <>{row.user_id}</>,
      },
      {
        id: 'name',
        title: 'Name',
        width: '20%',
        isUserSortable: true,
        cellContent: ({ row }) => <NavLink to={Routes.User.generatePath({ user_id: row.user_id })}>{row.name}</NavLink>,
      },
      {
        id: 'email',
        title: 'Email',
        width: '25%',
        isUserSortable: true,
        cellContent: ({ row }) => <>{row.email || '-'}</>,
      },
      {
        id: 'backoffice_role',
        title: 'Backoffice role',
        width: '12%',
        isUserSortable: true,
        cellContent: ({ row }) =>
          row.backoffice_role !== '-' ? <Pill kind="neutral">{beautifyString(row.backoffice_role)}</Pill> : <>-</>,
      },
      {
        id: 'credential_types_sort',
        title: 'Credentials',
        width: '15%',
        isUserSortable: true,
        cellContent: ({ row }) =>
          row.credential_types.length === 0 ? (
            <>-</>
          ) : (
            <Stack direction="row" gap="xsmall">
              {row.credential_types.includes(CredentialType.API_KEY) && <Pill kind="info">API key</Pill>}
              {row.credential_types.includes(CredentialType.EXT_SHIPMENT_STATUS_API_KEY) && (
                <Pill kind="info">EXT API key</Pill>
              )}
            </Stack>
          ),
      },
      {
        id: 'credential_types',
        title: 'View',
        width: 'auto',
        cellContent: ({ row }) => <NavLink to={Routes.User.generatePath({ user_id: row.user_id })}>User</NavLink>,
      },
    ],
    [],
  );

  const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);

  const containerRefCallback = useCallback((node: HTMLDivElement | null) => {
    if (node === null) return;
    let el: HTMLElement | null = node.parentElement;
    while (el !== null) {
      const { overflow, overflowY } = getComputedStyle(el);
      if (overflow === 'auto' || overflow === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') {
        setScrollParent(el);
        return;
      }
      el = el.parentElement;
    }
  }, []);

  if ((users ?? []).length === 0) {
    return <Text>No users</Text>;
  }

  return (
    <div ref={containerRefCallback}>
      {scrollParent !== null && (
        <InfiniteTable<UserRow>
          columns={columns}
          data={tableData}
          sortBy={sortBy}
          onSortByChange={setSortBy}
          scrollParent={scrollParent}
          className="compact-table"
        />
      )}
    </div>
  );
};
